import $ from 'jquery';
import { debounce, difference, find, forEach, get, isArray, isEqual, result } from 'lodash';
import { Formatter, PaymentCalculatorFactory } from '@dealersites/dealersite-js-core';

import {
  baseSearchParameters,
  drilldown_metadata,
  DSPFilter,
  FilterLookup,
  getParametersFromUrl,
  isNumeric,
  lockedScopeKeys,
  searchKeys,
} from './filters/index';
import { updateBuildAndPriceBanner } from './components/build-and-price-banners';
import { inventoryLocalization } from './translations';
import { InventorySearchEvents } from './events';
import { InventoryAPI, Renderer, ResultsParser } from './next';
import { buildSearchParams, updateUrl } from './next/url';
import { isSRPAsyncStrategy, SRPAsyncStrategy } from './srp-async-strategy';

const mainContent = document.getElementById('main');
const dspFiltersEl = mainContent ? mainContent.querySelector('#dsp-filters') : null;

if (mainContent && dspFiltersEl) {
  // Inventory selected with custom filters
  const selected = JSON.parse(dspFiltersEl.getAttribute('data-selected'));
  const filters = dspFiltersEl.querySelectorAll('.filter-container');

  const sortBy = mainContent.querySelector('#js-sort-select');
  const titleSearch = mainContent.querySelector('#dsp_title');
  const bnpBanner = mainContent.querySelector('.bnp-banner');
  const resetButton = mainContent.querySelector('#filter-reset');

  const parameterMap = new FilterLookup();

  // The scope the page itself pins (sale_type on a new/used SRP, organization on
  // a dealer page). DSPFilter promotes a `selected` entry into searchParameters
  // only when a filter container matching it exists, so anything without one has
  // to be carried manually or it is dropped from every filter/sort AJAX and the
  // results widen to the full inventory. Where a container does exist the value
  // is the visitor's to change, so leave it to DSPFilter.
  const filterContainerParams = new Set(
    Array.from(filters, (container) => container.dataset.searchParameter ?? container.id),
  );

  const lockedScope = {};
  forEach(lockedScopeKeys, (key) => {
    const value = get(selected, key);
    if (value && !filterContainerParams.has(key)) {
      lockedScope[key] = isArray(value) ? value.join(',') : value;
    }
  });

  let searchParameters = { ...baseSearchParameters, ...lockedScope };

  const dspFilterList = {};
  function setMakesAndModelsLists(selectorElement) {
    if (['make', 'model', 'category'].includes(selectorElement.searchParameter)) {
      dspFilterList[selectorElement.searchParameter] = selectorElement.options;
    }
  }

  function updatePaymentCalculations() {
    $('.vehiclePayments .js-payment').each(function () {
      let $payment = $(this);
      let calculatorInputs = {
        price: $payment.data('price'),
        down: $payment.data('down'),
        term: $payment.data('term'),
        rate: $payment.data('rate'),
        tradein: $payment.data('tradein'),
        tax: 0,
      };

      const calculatorType = $payment.data('calculator');

      if (calculatorType === 'lease') {
        calculatorInputs.residual = $payment.data('residual');
      }

      if (['finance', 'lease'].includes(calculatorType)) {
        const calculator = PaymentCalculatorFactory.createCalculator(
          calculatorType,
          calculatorInputs,
        );
        calculator.calculate();
        $payment.text(Formatter.price(calculator.payments.biweekly, 0));
      }
    });
  }

  async function getInventory({ parameters = searchParameters, keys = searchKeys } = {}) {
    const url = new URL(window.location);
    const useNewAsyncSearchResults = isSRPAsyncStrategy(SRPAsyncStrategy.NEW);

    const api = new InventoryAPI({ baseUrl: window.location.origin });
    const vehicleResultsRenderer = new Renderer({
      rootSelector: '#vehicle-results-container',
      clearChildrenSelector: '#vehicle-results',
      loaderSelector: '#loader',
    });

    vehicleResultsRenderer.clearChildren();
    vehicleResultsRenderer.showLoader();

    let customFilterId, pageId;
    if (useNewAsyncSearchResults) {
      customFilterId = $('[data-custom-filter]').data('custom-filter');
      pageId = $('[data-page]').data('page');
    }

    const searchParams = buildSearchParams({ parameters, keys, customFilterId, pageId });

    $(document).trigger(InventorySearchEvents.PRE_FETCH);

    let res;
    if (useNewAsyncSearchResults) {
      res = await api.loadAsyncWidget('search-results', { params: searchParams });
    } else {
      url.search = searchParams.toString();
      res = await api.getInventoryHTML(url.pathname, searchParams);
    }

    if (!res) {
      return {};
    }

    const parser = new ResultsParser({
      dataSelector: '#dsp-filters',
      dataAttribute: 'data-inventory',
      resultsSelector: '#vehicle-results',
    });
    const { results, data } = parser.parseResults(res.data);

    $(document).trigger(InventorySearchEvents.POST_FETCH, [data]);

    vehicleResultsRenderer.render(results);
    updatePaymentCalculations();

    // The locked scope stays out of the address bar. It is fixed by the page, so
    // it is not the visitor's state to carry around, and writing it there changes
    // the shape of `selected_filters` on the next server render — a shortcode
    // attribute arrives as ["used"] but a query param as "used", which silently
    // breaks the theme templates that branch on it. Re-apply it after the reset
    // instead, since the URL can no longer supply it.
    const newUrl = updateUrl({
      url,
      parameters,
      keys: difference(keys, lockedScopeKeys),
      filters: dspFilterList,
    });
    searchParameters = { ...getParametersFromUrl(newUrl), ...lockedScope };

    return data;
  }

  function updateFilter(parameter, value = null) {
    if (value !== null) {
      searchParameters[parameter] = value;
    }

    if (isNumeric(parameter)) {
      searchParameters[`${parameter}_min`] = this.searchParameters[`${parameter}_min`];
      searchParameters[`${parameter}_max`] = this.searchParameters[`${parameter}_max`];
      getInventory();
    } else {
      const paramIndex = drilldown_metadata.indexOf(parameter);

      if (paramIndex >= 0) {
        for (let param of drilldown_metadata) {
          const $el = $(`#${param}`);
          const $select = $el.find('> select');

          if ($select) {
            $select.html(`<option>${inventoryLocalization.gettext('Loading')}...</option>`);
          }
        }
      }

      switch (parameter) {
        case 'category': {
          searchParameters['make'] = null;
          searchParameters['model'] = null;
          searchParameters['trim'] = null;
          break;
        }

        case 'make': {
          searchParameters['model'] = null;
          searchParameters['trim'] = null;
          break;
        }

        case 'model': {
          searchParameters['trim'] = null;
          break;
        }
      }

      getInventory().then((data) => {
        if (searchKeys.indexOf(parameter) >= 0) {
          for (let param of searchKeys) {
            for (const filterId of parameterMap.getByParameter(param)) {
              let element = document.getElementById(filterId);
              if (element) {
                let newFilter = new DSPFilter(element, updateFilter, searchParameters, data);
                newFilter.create();
                setMakesAndModelsLists(newFilter);
              }
            }
          }
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    forEach(getParametersFromUrl(window.location), (value, key) => {
      searchParameters[key] = value;
    });

    if (resetButton) {
      resetButton.addEventListener('click', function () {
        window.location = location.origin + location.pathname;
      });
    }

    if (sortBy) {
      sortBy.addEventListener(
        'change',
        debounce((event) => {
          searchParameters.sort = event.target.value;
          getInventory();
        }, 500),
      );
    }

    if (titleSearch) {
      titleSearch.addEventListener(
        'input',
        debounce((event) => {
          searchParameters.title = event.target.value;
          getInventory();
        }, 500),
      );
    }

    const submitButton = mainContent.querySelector('#filter-submit');
    const isDynamic = dspFiltersEl.getAttribute('data-dynamic') === 'true';
    if (isDynamic) {
      submitButton?.parentElement?.classList.add('d-none');
    } else {
      submitButton?.addEventListener('click', function () {
        getInventory().then(() => {
          filters.forEach((filterContainer) => {
            updateFilter(filterContainer.id);
          });
        });
      });
    }

    const inventoryData = JSON.parse(dspFiltersEl.getAttribute('data-inventory'));
    filters.forEach((filterContainer) => {
      const filter = new DSPFilter(
        filterContainer,
        isDynamic ? updateFilter : undefined,
        searchParameters,
        inventoryData,
        selected,
      );

      parameterMap.add(filter.searchParameter, filter.containerId);

      filter.create();
      setMakesAndModelsLists(filter);

      if (bnpBanner && ['make', 'model'].includes(filterContainer.id)) {
        filterContainer.addEventListener('dspFilter.change', function () {
          updateBuildAndPriceBanner(bnpBanner);
        });
      }
    });

    // update bnp banner on page load
    if (bnpBanner && (searchParameters['make'] || searchParameters['model'])) {
      updateBuildAndPriceBanner(bnpBanner);
    }
  });
}
